import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ShoppingCart, Send, X, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, formatINR } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Section } from "@/components/Section";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import WhatsAppInquireButton from "@/components/WhatsAppInquireButton";
import MediaLightbox from "@/components/MediaLightbox";

const ORDER_INITIAL = { quantity: 1, name: "", email: "", phone: "", company: "", gst_number: "", address: "", city: "", state: "Bihar", pincode: "", message: "" };

export default function SpareDetail() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [orderOpen, setOrderOpen] = useState(false);
  const [form, setForm] = useState(ORDER_INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxStart, setLightboxStart] = useState(0);

  const gallery = useMemo(() => {
    if (!s) return [];
    const arr = [{ type: "image", src: s.image }];
    (s.images || []).filter((u) => u && u !== s.image).forEach((src) => arr.push({ type: "image", src }));
    return arr;
  }, [s]);

  useEffect(() => {
    api.get(`/spares/${slug}`).then((r) => setS(r.data)).catch(() => setErr("Spare not found"));
  }, [slug]);

  const submitOrder = async (e) => {
    e.preventDefault();
    if (!s) return;
    setSubmitting(true);
    try {
      const payload = { ...form, spare_id: s.id, spare_title: s.title, quantity: Number(form.quantity) || 1 };
      const { data } = await api.post("/spares/order", payload);
      toast.success(data.message || "Order received!");
      setOrderOpen(false);
      setForm(ORDER_INITIAL);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Failed to submit");
    } finally { setSubmitting(false); }
  };

  if (err) return (
    <Section className="py-32 text-center">
      <h2 className="font-heading font-black text-3xl">{err}</h2>
      <Link to="/spares" className="btn-outline mt-6 inline-flex"><ArrowLeft className="w-4 h-4" /> Back to Spares</Link>
    </Section>
  );
  if (!s) return <Section className="py-32"><div className="skeleton h-96" /></Section>;

  const discounted = s.discount_percent ? s.price * (1 - s.discount_percent / 100) : s.price;
  const inStock = (s.stock_qty || 0) > 0;

  return (
    <>
      <section className="bg-[#1A1A1A] text-white pt-10 pb-8 noise-overlay">
        <Section>
          <Link to="/spares" className="text-xs uppercase tracking-widest text-gray-400 hover:text-[#FFCC00] inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3" /> Back to Spares</Link>
          <div className="mt-4 label-eyebrow">{s.brand} • {s.category}</div>
          <h1 className="heading-xl text-3xl sm:text-4xl lg:text-5xl mt-2 text-white" data-testid="spare-title">{s.title}</h1>
          {s.part_number && <div className="text-sm text-gray-400 font-mono mt-2">Part Number: {s.part_number}</div>}
        </Section>
      </section>

      <Section className="py-12 grid lg:grid-cols-2 gap-10">
        <div>
          <button
            type="button"
            onClick={() => { setLightboxStart(0); setLightboxOpen(true); }}
            className="aspect-[4/3] w-full bg-gray-100 overflow-hidden border border-gray-200 group cursor-zoom-in relative block"
            aria-label="Open image viewer"
            data-testid="spare-gallery-main"
          >
            <img src={mediaUrl(s.image)} alt={s.title} className="w-full h-full object-contain bg-white transition-transform group-hover:scale-[1.02]" />
            <div className="absolute top-3 right-3 bg-black/70 text-white px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold opacity-0 group-hover:opacity-100 transition-opacity">
              Click to expand
            </div>
          </button>
          {s.images && s.images.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-3" data-testid="spare-gallery-thumbs">
              {s.images.slice(0, 4).map((img, i) => (
                <button
                  type="button"
                  key={i}
                  onClick={() => {
                    const idx = gallery.findIndex((m) => m.src === img);
                    setLightboxStart(idx >= 0 ? idx : 0);
                    setLightboxOpen(true);
                  }}
                  className="aspect-square overflow-hidden border border-gray-200 hover:border-[#E65C00] transition-colors"
                  aria-label={`Open image ${i + 1}`}
                ><img src={mediaUrl(img)} alt="" className="w-full h-full object-cover" /></button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="border-l-4 border-[#E65C00] pl-4">
            <div className="text-[10px] uppercase tracking-widest text-gray-500">Price</div>
            <div className="flex items-baseline gap-3">
              <div className="font-heading font-black text-4xl">{formatINR(discounted)}</div>
              {s.discount_percent > 0 && <div className="text-lg text-gray-400 line-through">{formatINR(s.price)}</div>}
              {s.discount_percent > 0 && <span className="bg-[#FFCC00] text-[#1A1A1A] px-2 py-1 text-xs font-black uppercase tracking-widest">SAVE {s.discount_percent}%</span>}
            </div>
            <div className={`mt-2 text-sm font-bold ${inStock ? "text-green-700" : "text-red-600"}`}>
              {inStock ? `In Stock · ${s.stock_qty} units available` : "Out of Stock — order for next batch"}
            </div>
          </div>

          {s.compatible_with && (
            <div className="mt-5 bg-gray-50 border border-gray-200 p-4">
              <div className="text-[10px] uppercase tracking-widest font-bold text-gray-500">Compatible With</div>
              <div className="mt-1 text-sm">{s.compatible_with}</div>
            </div>
          )}

          <p className="mt-6 text-gray-700 leading-relaxed">{s.description}</p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={() => setOrderOpen(true)} className="btn-primary" data-testid="spare-order-cta">
              <ShoppingCart className="w-4 h-4" /> Place Order
            </button>
            <WhatsAppInquireButton itemTitle={s.title} kind="spare" className="px-5 py-2.5 text-xs" />
            {s.brochure_url && (
              <a
                href={mediaUrl(s.brochure_url)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 bg-[#FFCC00] hover:bg-[#E65C00] hover:text-white text-[#1A1A1A] px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
                data-testid="spare-brochure"
              >
                <Download className="w-3.5 h-3.5" /> Brochure (PDF)
              </a>
            )}
            <Link
              to="/spares"
              className="inline-flex items-center gap-2 bg-[#1A1A1A] hover:bg-black text-white px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-colors"
              data-testid="spare-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
          </div>
        </div>
      </Section>

      {s.specs && Object.keys(s.specs).length > 0 && (
        <Section className="pb-20">
          <h2 className="heading-xl text-2xl sm:text-3xl mb-6">SPECIFICATIONS</h2>
          <div className="border border-gray-300">
            <table className="w-full">
              <tbody>
                {Object.entries(s.specs).map(([k, v], i) => (
                  <tr key={k} className={i % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-5 py-3 font-bold text-sm w-1/3 border-b border-gray-200">{k}</td>
                    <td className="px-5 py-3 text-sm border-b border-gray-200">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Order Form Dialog */}
      <Dialog open={orderOpen} onOpenChange={setOrderOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="spare-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase">Order — <span className="text-[#E65C00]">{s.title}</span></DialogTitle>
          </DialogHeader>
          <form onSubmit={submitOrder} className="space-y-4 mt-2">
            <div className="bg-[#FFCC00]/20 border-l-4 border-[#E65C00] p-3 text-sm">
              <strong>Unit Price:</strong> {formatINR(discounted)} · <strong>Qty:</strong>
              <input type="number" min={1} className="ml-2 w-20 px-2 py-1 border border-gray-300 focus:outline-none focus:border-[#E65C00]" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) || 1 })} data-testid="order-qty" />
              <span className="ml-3"><strong>Subtotal:</strong> {formatINR(discounted * (Number(form.quantity) || 1))}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Full Name *" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="order-name" />
              <Input label="Mobile *" required value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="order-phone" />
            </div>
            <Input label="Email *" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="order-email" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Company Name" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="order-company" />
              <Input label="GST Number" value={form.gst_number} onChange={(v) => setForm({ ...form, gst_number: v })} testid="order-gst" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Delivery Address *</label>
              <textarea required rows={2} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="order-address" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Input label="City *" required value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="order-city" />
              <Input label="State *" required value={form.state} onChange={(v) => setForm({ ...form, state: v })} testid="order-state" />
              <Input label="Pincode *" required value={form.pincode} onChange={(v) => setForm({ ...form, pincode: v })} testid="order-pincode" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">Additional Notes</label>
              <textarea rows={2} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" placeholder="e.g. urgent dispatch, preferred courier..." value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="order-message" />
            </div>
            <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white py-2">
              <button type="button" onClick={() => setOrderOpen(false)} className="btn-outline text-sm"><X className="w-4 h-4" /> Cancel</button>
              <button type="submit" disabled={submitting} className="btn-primary text-sm" data-testid="order-submit">
                {submitting ? "Submitting..." : <><Send className="w-4 h-4" /> Submit Order</>}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <MediaLightbox items={gallery} open={lightboxOpen} startIndex={lightboxStart} onClose={() => setLightboxOpen(false)} />
    </>
  );
}

function Input({ label, type = "text", value, onChange, testid, required }) {
  const name = testid ? testid.replace(/^order-/, "") : undefined;
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">{label}</label>
      <input type={type} name={name} required={required} className="mt-2 w-full px-3 py-2 border border-gray-300 focus:outline-none focus:border-[#E65C00] text-sm" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
